import React, { FC, useState } from 'react';
import './PermissionLayout.css';
import {
    DataTableConfig,
    DataTableProvider,
    DataTable,
    useBreakPoint,
    DataCardList,
    ActionButton
} from '@/Shared';

import { PermissionModel, PermissionViewModel } from '../../Logic';

interface PermissionLayoutProps {
    tableConfig: DataTableConfig<any>
}

/**
 * =====================================================
 * Component: PermissionLayout
 * =====================================================
 *
 * Description:
 * React component responsible for rendering permission tables per role.
 * Each role gets its own table displaying the access rights per resource.
 *
 * Responsibilities:
 * - Iterate over the PermissionViewModel list (one per role)
 * - For each role, render a DataTableProvider with:
 *    - initialData: the list of ResourceAccessViewModel ready to display
 *    - headers: ResourceAccess headers with pre-formatted display values (✅ / ❌ or ReactNode)
 *    - title: the table title based on the role
 * - Allow generic DataTable to handle rendering, sorting, and responsive layout
 *
 * Notes:
 * - The ResourceAccessViewModel prepares the data for display (ready-to-render),
 *   so the DataTable does not need to transform booleans into icons.
 * - Sorting on icon columns can use a comparator or underlying boolean flag
 *   in ResourceAccessViewModel if needed.
 *
 * Author: Samano CASTRE
 * Date: July 17, 2025
 */
const PermissionLayout: FC<PermissionLayoutProps> = ({ tableConfig }) => {

    const list = tableConfig.initialData as PermissionViewModel[] ?? [];
    const [selectedPermission, setSelectedPermission] = useState<PermissionViewModel>(list[0] ?? null);
    const { isMobile, isTablet, isDesktop } = useBreakPoint();

    if (!selectedPermission) return null;

    const getHeaderActions = () => (<div className="sub-actions flex-row">
        {
            list.map(item =>
                <ActionButton
                    key={item.id}
                    onClick={() => setSelectedPermission(item)}
                    label={item.role}
                    showLabel
                    classNames={item.id === selectedPermission.id ? 'selected-permission' : ''}
                />
            )
        }
    </div>)

    return (
            <DataTableProvider
                config={{
                    ...tableConfig,
                    initialData: selectedPermission.resourceAccessList,
                    headers: selectedPermission.resourceAccessHeaders,
                    title: selectedPermission.title,
                    headerAction : getHeaderActions()
                }}
            >
                {isDesktop && <DataTable />}
                {(isMobile || isTablet) && <DataCardList />}
            </DataTableProvider>
    );
}

export default PermissionLayout;